import React from "react";
import {
  Box,
  Typography,
  IconButton,
  Card,
  CardMedia,
  CardContent,
  useTheme,
  Chip,
  Tooltip,
} from "@mui/material";
import FavoriteIcon from "@mui/icons-material/Favorite";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import BedIcon from "@mui/icons-material/Bed";
import BathtubIcon from "@mui/icons-material/Bathtub";
// import HotelIcon from "@mui/icons-material/Hotel";
import MaleIcon from "@mui/icons-material/Male";
import FemaleIcon from "@mui/icons-material/Female";
import ReadIcon from "@mui/icons-material/AutoStories";
import WorkIcon from "@mui/icons-material/Work";
import GroupIcon from "@mui/icons-material/Group";
import SquareFootIcon from "@mui/icons-material/SquareFoot";
import makeFullUrl from "../../utils/cloudinary";
import PriceDisplay from "../PriceDisplay";

const PropertyCardVertical = ({ property, isFavorite, onFavoriteToggle }) => {
  const theme = useTheme();

  // const formattedPrice = new Intl.NumberFormat("en-AE", {
  //   style: "currency",
  //   currency: "AED",
  //   minimumFractionDigits: property.units[0].price % 1 !== 0 ? 2 : 0, // Ensures two decimal places if available
  //   maximumFractionDigits: 2, // Limits to two decimal places
  // }).format(property.units[0].price);

  // Helper function to get the display value from a key
  const getDisplayValue = (key, options) => {
    const option = options.find((opt) => opt.key === key);
    return option ? option.value : key; // Fallback to key if no match is found
  };

  // List of Payment Fequency for dropdown menu
  const paymentFrequencyArray = [
    { key: "monthly", value: "Monthly" },
    { key: "yearly", value: "Yearly" },
    { key: "daily", value: "Daily" },
  ];

  const imageUrl = makeFullUrl(
    property.mainImage.filePath,
    property.mainImage.fileType
  );

  // List of Bedrooms for dropdown menu
  const bedroomsArray = [
    { key: "studio", value: "Studio" },
    { key: "1", value: "1" },
    { key: "2", value: "2" },
    { key: "3", value: "3" },
    { key: "4", value: "4" },
    { key: "5", value: "5" },
    { key: "5+", value: "5+" },
  ];

  const bathroomTypeArray = [
    { key: "attached", value: "Attached" },
    { key: "common", value: "Common" },
    // { key: "shared", value: "Shared" },
    { key: "2", value: "2" },
    { key: "3", value: "3" },
    { key: "4", value: "4" },
    { key: "5", value: "5" },
    { key: "5+", value: "5+" },
  ];

  const getGenderBadge = (type) => {
    // Replace 'type' with property.gender or property.allowedGuests
    if (type === "female")
      return {
        label: "Women",
        color: "#cf5e93",
        // color: "pink",
        icon: <FemaleIcon fontSize="small" />,
      };
    if (type === "male")
      return {
        label: "Men",
        color: "info.main",
        icon: <MaleIcon fontSize="small" />,
      };
    return { label: "Family/All", color: "success.main", icon: null };
  };

  const getTenantTypeBadge = (type) => {
    // Replace 'type' with property.gender or property.allowedGuests
    if (type === "students")
      return {
        label: "Students",
        color: "success.main",
        // color: "pink",
        icon: <ReadIcon fontSize="small" />,
        tooltip: null,
      };
    if (type === "professionals")
      return {
        label: "Professionals",
        color: "success.main",
        icon: <WorkIcon fontSize="small" />,
        tooltip: null,
      };
    return {
      // label: "Students & Professionals",
      label: "Both",
      color: "success.main",
      icon: <GroupIcon fontSize="small" />,
      tooltip: "Students & Professionals",
    };
  };

  const genderBadge = getGenderBadge(property.gender || "all"); // Default to all if undefined
  const tenantBadge = getTenantTypeBadge(property.tenantType || "all"); // Default to all if undefined

  // Safely access the first unit to prevent crashes
  const unit =
    property.units && property.units.length > 0 ? property.units[0] : null;

  // If no unit, we shouldn't render the card or render a fallback state.
  // For now, we render even if empty but handle nulls.
  if (!property) return null;

  return (
    <Card
      sx={{
        width: "100%",
        maxWidth: 270, // Standard card width, remove if using Grid
        // maxWidth: "100%",
        borderRadius: "12px", // Matches modern rounded aesthetic
        boxShadow: "none", // Flat by default like Airbnb
        cursor: "pointer",
        position: "relative",
        transition: "transform 0.2s, box-shadow 0.2s",
        // "&:hover": {
        //   transform: "translateY(-4px)",
        //   boxShadow: theme.shadows[4],
        // },
      }}
    >
      {/* 1. Image Area with Overlay Heart */}
      <Box sx={{ position: "relative", p: 1 }}>
        <CardMedia
          component="img"
          image={
            // property.images?.[0]?.url ||
            // property.mainImage ||
            // "/default-property.jpg"
            imageUrl
          }
          alt={property.title}
          sx={{
            height: 240, // Fixed height for consistency
            width: "100%",
            borderRadius: "10px", // Rounded corners on image
            objectFit: "cover",
          }}
        />
        <Chip
          label={genderBadge.label}
          icon={genderBadge.icon}
          size="small"
          variant="outlined"
          sx={{
            position: "absolute",
            top: 15,
            left: 15,
            bgcolor: "white",
            fontWeight: "bold",
            color: genderBadge.color,
            borderColor: genderBadge.color,
            "& .MuiChip-icon": { color: genderBadge.color },
          }}
        />
        <Tooltip
          // placement="top"
          title={tenantBadge.tooltip || ""}
          disableHoverListener={!tenantBadge.tooltip} // Disable if no tooltip exists
          arrow
        >
          <Chip
            label={tenantBadge.label}
            icon={tenantBadge.icon}
            size="small"
            variant="outlined"
            sx={{
              position: "absolute",
              top: 15,
              right: 15,
              bgcolor: "white",
              fontWeight: "bold",
              color: tenantBadge.color,
              borderColor: tenantBadge.color,
              "& .MuiChip-icon": { color: tenantBadge.color },
              pl: 0.5,
            }}
          />
        </Tooltip>

        {/* Absolute Positioned Favorite Button */}
        {/* <IconButton
          onClick={(e) => {
            e.stopPropagation();
            onFavoriteToggle(property.id || property._id);
          }}
          sx={{
            position: "absolute",
            top: 16,
            right: 16,
            bgcolor: "rgba(255, 255, 255, 0.7)",
            backdropFilter: "blur(2px)",
            padding: "6px",
            "&:hover": {
              bgcolor: "rgba(255, 255, 255, 0.9)",
            },
          }}
          size="small"
        >
          <FavoriteIcon
            fontSize="small"
            color={isFavorite ? "error" : "disabled"}
          />
        </IconButton> */}

        {/* Optional: Property Type Badge over image */}
        {/* <Chip
          label={property.propertyTypeName || "Property"}
          size="small"
          sx={{
            position: "absolute",
            top: 16,
            left: 16,
            bgcolor: "rgba(255,255,255,0.9)",
            fontWeight: 600,
            fontSize: "0.7rem",
            height: 24,
          }}
        /> */}
      </Box>

      {/* 2. Content Area */}
      {/* <CardContent sx={{ pt: 1, pb: "16px !important", px: 1.5 }}> */}
      <CardContent sx={{ pt: 1, pb: "16px !important", px: 1.5 }}>
        {/* Title & Location */}
        <Box sx={{ mb: 1 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
            }}
          >
            <Typography
              variant="subtitle1"
              component="h3"
              sx={{ fontWeight: 500, lineHeight: 1 }}
            >
              {property.title || property.propertyType?.name}
              {/* {property.propertyType.name} */}
              {/* {unit.unitType.name || property.propertyType.name} */}
            </Typography>
            {/* Rating could go here if you had it */}
          </Box>

          <Typography
            // variant="body2"
            variant="subtitle2"
            color="text.secondary"
            sx={{ display: "flex", alignItems: "center", mt: 1 }}
          >
            <LocationOnIcon
              sx={{
                fontSize: 16,
                mr: 0.5,
                color: theme.palette.text.secondary,
              }}
            />
            {/* {property.location || `${property.city}, ${property.country}`} */}
            {`${property?.address?.area}, ${property?.address?.city?.name}`}
          </Typography>
        </Box>

        {/* Specs Row (Beds / Baths / Size) */}
        {unit ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              // mt: 1,
              color: "text.secondary",
            }}
          >
            {/* Beds */}
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <BedIcon
                sx={{ fontSize: 18, mr: 0.5, transform: "translateY(-1px)" }}
              />
              <Typography variant="subtitle2">
                {/* {getDisplayValue(property.units[0].bedrooms, bedroomsArray)} */}
                {unit.unitType.occupancyCount
                  ? unit.unitType.occupancyCount
                  : getDisplayValue(unit.bedrooms, bedroomsArray)}
              </Typography>
            </Box>

            {/* Dot Separator */}
            {/* <Typography variant="caption" sx={{ color: "text.disabled" }}>
              •
            </Typography> */}

            {/* Baths */}
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <BathtubIcon
                sx={{ fontSize: 18, mr: 0.5, transform: "translateY(-1px)" }}
              />
              <Typography variant="subtitle2">
                {/* {unit.bathroomType} */}
                {getDisplayValue(unit.bathroomType, bathroomTypeArray)}
              </Typography>
            </Box>

            {/* Dot Separator */}
            {/* <Typography variant="caption" sx={{ color: "text.disabled" }}>
              •
            </Typography> */}

            {/* Size */}
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <SquareFootIcon
                sx={{ fontSize: 18, mr: 0.5, transform: "translateY(-1px)" }}
              />
              <Typography variant="subtitle2">{unit.size} sqft</Typography>
            </Box>
          </Box>
        ) : (
          <Typography variant="caption" color="error">
            Unit info unavailable
          </Typography>
        )}

        {/* Short Comment Snippet (Optional - kept per request) */}
        {/* {unit?.comments && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mb: 1.5,
              display: "-webkit-box",
              overflow: "hidden",
              WebkitBoxOrient: "vertical",
              WebkitLineClamp: 1, // Force single line with ellipsis
              fontSize: "0.8rem",
            }}
          >
            {unit.comments}
          </Typography>
        )} */}

        {/* Price Section */}
        <Box sx={{ display: "flex", alignItems: "baseline" }}>
          {/* <Box
          sx={{
            display: "flex",
            alignItems: "center",
            // gap: 2,
            // mt: 1,
            // color: "text.secondary",
          }}
        > */}
          {/* <Typography variant="h6" component="span" sx={{ fontWeight: "bold" }}> */}
          {/* <Typography
            variant="subtitle1"
            component="h3"
            sx={{ fontWeight: 500, lineHeight: 1 }}
          >
            {formattedPrice}
          </Typography> */}
          {/* <Typography variant="subtitle2" fontWeight="bold"> */}
          <PriceDisplay
            // variant="subtitle1"
            // component="h3"
            sx={{
              fontWeight: 500,
              lineHeight: 1,
              mt: 1,
              transform: "translateY(-1px)",
            }}
            iconSize={17}
            textSx={{ fontSize: 16 }}
            unitPrice={unit.price}
          />
          {/* </Typography> */}

          {/* <PriceDisplay
            unitPrice={unit.price}
            sx={{ fontWeight: "bold", fontSize: 20 }}
          /> */}
          {/* {unit && (
            <Chip
              label={getDisplayValue(
                unit.paymentFrequency,
                paymentFrequencyArray
              )}
              color="inherit"
              sx={{ ml: 1 }}
              // size="small"
              // sx={{ backgroundColor: "black", color: "white" }}
            />
            // <Typography
            //   variant="body2"
            //   component="span"
            //   color="text.secondary"
            //   sx={{ ml: 0.5 }}
            // >
            //   / {getDisplayValue(unit.paymentFrequency, paymentFrequencyArray)}
            // </Typography>
          )} */}
        </Box>
      </CardContent>
    </Card>
  );
};

export default PropertyCardVertical;
